import {Tag, TagColor, TagSize} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {pluralize} from 'BenchlingUtil';
import ChildImportPlanSection from 'config-migration/import/import-plan-sections/ChildImportPlanSection';
import {LogLevel} from 'config-migration/migration-log/LogLevel';
import {HighImpactChange, MigrationError} from 'config-migration/MigrationConstants';
import {InlineErrorIcon, InlineWarningIcon} from 'config-migration/shared-ui-components/InlineIcon';
import {CollapsibleTriangle} from 'config-migration/shared-ui-components/TriangleToggle';
import {PrimaryObjectPlan} from 'config-migration/types/MigrationObjects';
import {MigrationTab} from 'config-migration/types/MigrationTab';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as Colors from 'ui-library/Colors';
import {GRAY_DARK, ORANGE_LIGHTEST, RED_POWDER} from 'ui-library/Colors';
import Popover from 'ui-library/floating-ui/Popover';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import {ObjectActionType} from '../../types/objectActions';
import {countChildHighImpactChanges} from '../HighImpactChanges';
import ObjectActionDropdown from '../ObjectActionDropdown';

export interface ImportPlanSectionRowProps {
  plan: PrimaryObjectPlan;
  errors: Array<MigrationError>;
  nestedDepth: number;
  /**
   * Controls whether the dropdown is shown and modifiable. If true, the default action is just shown as text.
   */
  showActionInline?: boolean;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  highImpactChangeMap: Map<string, Array<HighImpactChange>>;
}

export default function ImportPlanSectionRow(props: ImportPlanSectionRowProps): JSX.Element {
  const {plan, errors} = props;
  const hasErrors = errors.filter(({level}) => level === LogLevel.ERROR).length > 0;
  const hasWarnings = errors.filter(({level}) => level === LogLevel.WARNING).length > 0;
  const hasChildren = Boolean(plan.childPlansByType.size);
  const [isCollapsed, setIsCollapsed] = React.useState<boolean>(true);
  const highImpactChangeCount = countChildHighImpactChanges(plan, props.highImpactChangeMap);
  return (
    <>
      <ImportPlanSectionRowWrapper
        plan={plan}
        hasErrors={hasErrors}
        hasWarnings={hasWarnings}
        key={plan.objectId}
        nestedDepth={props.nestedDepth}
      >
        <ObjectDisplayNameColumn
          hasErrors={hasErrors}
          hasWarnings={hasWarnings}
          plan={plan}
          errors={errors}
          setIsCollapsed={setIsCollapsed}
          isCollapsed={isCollapsed}
          hasChildren={hasChildren}
          highImpactChangeCount={highImpactChangeCount}
        />
        <ObjectActionDropdownColumn
          plan={plan}
          onUpdatePlan={props.onUpdatePlan}
          showActionInline={props.showActionInline}
        />
      </ImportPlanSectionRowWrapper>
      {!isCollapsed &&
        hasChildren &&
        Array.from(plan.childPlansByType.keys()).map((childType) => (
          <ChildImportPlanSection
            plan={props.plan}
            childType={childType}
            onUpdatePlan={props.onUpdatePlan}
            nestedDepth={props.nestedDepth}
            highImpactChangeMap={props.highImpactChangeMap}
            key={childType}
          />
        ))}
    </>
  );
}

export interface ImportPlanSectionRowWrapperProps {
  plan: PrimaryObjectPlan;
  hasErrors: boolean;
  hasWarnings: boolean;
  children: ReactNode;
  nestedDepth: number;
}

export function ImportPlanSectionRowWrapper(props: ImportPlanSectionRowWrapperProps): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-row',
        props.nestedDepth !== 0 && css(styles.childRow),
        props.plan.selectedAction.type === ObjectActionType.IGNORE && css(styles.ignoreRow),
        props.hasErrors && css(styles.errorRow),
        props.hasWarnings && !props.hasErrors && css(styles.warningRow)
      )}
      {...getTestProps({component: 'ImportPlanSectionRow', element: 'tableRow'})}
    >
      {props.children}
    </div>
  );
}

export function ObjectDisplayNameColumn(props: {
  hasErrors: boolean;
  hasWarnings: boolean;
  rowIcon?: BenchlingIconName;
  subLabel?: string;
  plan: PrimaryObjectPlan;
  errors: Array<MigrationError>;
  setIsCollapsed: (isCollapsed: boolean) => void;
  isCollapsed: boolean;
  hasChildren: boolean;
  highImpactChangeCount: number;
}): JSX.Element {
  return (
    <div
      className={classNames(
        'simpleDivTable-cell',
        props.subLabel ? css(styles.subLabelHeight) : null,
        props.hasErrors || props.hasWarnings ? css(styles.iconCell) : css(styles.firstCell),
        'u-flex-display u-vertical-center-items'
      )}
    >
      <div>
        {(() => {
          if (props.hasErrors) {
            return (
              <InlineErrorIcon
                objectName={props.plan.objectDisplayName}
                migrationTab={MigrationTab.IMPORT}
                errors={props.errors}
              />
            );
          } else if (props.hasWarnings) {
            return (
              <InlineWarningIcon
                objectName={props.plan.objectDisplayName}
                migrationTab={MigrationTab.IMPORT}
                errors={props.errors}
              />
            );
          }
          return null;
        })()}
        {props.rowIcon ? <BenchlingIcon className="result-icon" icon={props.rowIcon} /> : null}
        <span
          onClick={() => {
            props.setIsCollapsed(!props.isCollapsed);
          }}
          className={classNames(
            (() => {
              if (props.hasChildren && props.rowIcon) {
                return css(styles.parentToggle, styles.toggleWithIcon);
              } else if (props.hasChildren) {
                return css(styles.parentToggle);
              } else {
                return css(styles.noToggle);
              }
            })()
          )}
          {...(props.hasChildren &&
            getTestProps({component: 'ImportPlanSectionRow', element: 'collapseToggle'}))}
        >
          {props.hasChildren ? <CollapsibleTriangle isCollapsed={props.isCollapsed} /> : null}
          {props.plan.objectDisplayName}
          {props.highImpactChangeCount > 0 && (
            <span className={css(styles.highImpactParentIcon)}>
              <Popover renderContent={() => getParentIconHoverText(props.highImpactChangeCount)}>
                <Tag
                  text={props.highImpactChangeCount.toString()}
                  color={TagColor.WARN}
                  size={TagSize.SMALL}
                />
              </Popover>
            </span>
          )}
        </span>
      </div>
      {props.subLabel ? <div className={css(styles.subLabel)}>{props.subLabel}</div> : null}
    </div>
  );
}

function getParentIconHoverText(highImpactChangeCount: number): string {
  return `${highImpactChangeCount} high impact ${pluralize(highImpactChangeCount, 'change')} to this object`;
}

export function ObjectActionDropdownColumn(props: {
  plan: PrimaryObjectPlan;
  onUpdatePlan: (plan: PrimaryObjectPlan) => void;
  showActionInline?: boolean;
}): JSX.Element {
  // Check if any action is an ignore action
  const hasIgnoreAction =
    props.plan.initialAction.type === ObjectActionType.IGNORE ||
    props.plan.selectedAction.type === ObjectActionType.IGNORE;

  return (
    <div className="simpleDivTable-cell">
      <ObjectActionDropdown
        object={props.plan}
        showActionInline={props.showActionInline}
        excludeIgnore={hasIgnoreAction}
        onUpdateObject={(plan: PrimaryObjectPlan) => {
          props.onUpdatePlan({
            ...plan,
            childPlansByType: new Map(),
          });
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  iconCell: {
    marginLeft: -6,
    paddingRight: 18,
  },
  firstCell: {
    marginLeft: 15,
  },
  warningRow: {
    backgroundColor: ORANGE_LIGHTEST,
  },
  errorRow: {
    backgroundColor: RED_POWDER,
  },
  ignoreRow: {
    color: GRAY_DARK,
  },
  parentToggle: {
    cursor: 'pointer',
  },
  toggleWithIcon: {
    marginLeft: 7,
  },
  noToggle: {
    marginLeft: 15,
  },
  childRow: {
    backgroundColor: Colors.GRAY_LIGHTER,
    border: 'none',
  },
  subLabel: {
    display: 'inline-block',
    minWidth: '0',
    marginLeft: 28,
  },
  subLabelHeight: {
    lineHeight: '14px',
  },
  highImpactParentIcon: {
    marginLeft: 6,
  },
});
